import React, { useContext, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import AuthContext from "context/AuthContext";
import Pagination from "../Pagination";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { isEmpty, startCase } from "lodash";

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }
  return String(value);
};

function ProjectHistory() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const location = useLocation();
  const item = location?.state?.item;

  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
  });

  const fetchHistory = async () => {
    try {
      const result = await apiGet(`${apiPath.projectPath}/${id}/history`, {
        page,
        pageSize,
      });
      const response = result?.data?.results;
      const resultStatus = result?.data?.success;
      setList(response?.docs || []);
      setPaginationObj({
        page: resultStatus ? response.page : 1,
        pageCount: resultStatus ? response.totalPages : 1,
        perPageItem: resultStatus ? response?.docs?.length : 0,
        totalItems: resultStatus ? response.totalDocs : 0,
        pageRangeDisplayed: 10,
      });
    } catch (error) {
      console.log("error fetching project history", error.message);
    }
  };

  useEffect(() => {
    if (user?.role === "admin" && id) {
      fetchHistory();
    }
  }, [page, id, user?.role]);

  if (user?.role !== "admin") {
    return <Navigate to="/projects" replace />;
  }

  const title = item?.title || item?.titleAr || "Project";

  return (
    <div className="bg-white">
      <div className="px-5 py-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">
              Change History
            </h1>
            <p className="text-sm text-slate-500 mt-1">{title}</p>
          </div>
          <Link
            to="/projects"
            className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50"
          >
            {t("O_BACK") || "Back"}
          </Link>
        </div>

        <div className="overflow-x-auto relative shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                <th className="py-3 px-6">When</th>
                <th className="py-3 px-6">Who</th>
                <th className="py-3 px-6">Action</th>
                <th className="py-3 px-6">Changes</th>
              </tr>
            </thead>
            <tbody>
              {!isEmpty(list) &&
                list.map((row) => (
                  <tr
                    key={row._id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <td className="py-4 px-6 whitespace-nowrap">
                      {row.createdAt
                        ? dayjs(row.createdAt).format("DD MMM YYYY, hh:mm A")
                        : "—"}
                    </td>
                    <td className="py-4 px-6">
                      {row.changedByName || "—"}
                    </td>
                    <td className="py-4 px-6">
                      {startCase(row.action || "")}
                    </td>
                    <td className="py-4 px-6">
                      {!isEmpty(row.changes) ? (
                        <ul className="space-y-1.5 max-w-xl">
                          {row.changes.map((change, idx) => (
                            <li
                              key={`${row._id}-${idx}`}
                              className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                            >
                              <span className="font-semibold text-slate-800">
                                {change.field}
                              </span>
                              {": "}
                              <span className="text-red-600 break-all bg-red-50 px-1 rounded">
                                {formatValue(change.oldValue)}
                              </span>
                              {" → "}
                              <span className="text-green-700 break-all bg-green-50 px-1 rounded">
                                {formatValue(change.newValue)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : row.snapshot ? (
                        <span className="text-xs text-slate-500">
                          Snapshot recorded ({startCase(row.action)})
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              {isEmpty(list) && (
                <tr className="bg-white border-b w-full text-center">
                  <td className="py-4 px-6" colSpan={4}>
                    {t("O_NO_RECORD_FOUND")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          handlePageClick={(event) => setPage(event.selected + 1)}
          options={paginationObj}
          page={page}
        />
      </div>
    </div>
  );
}

export default ProjectHistory;
